import { AsyncPipe, DatePipe, KeyValuePipe } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BehaviorSubject, Subject, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Bundle, Communication } from 'fhir/r4';
import { backendEndPointToken } from '../app.config';
import { MessagingContextService } from './messaging-context.service';
import { MessagingMessagesService } from './messaging-messages.service';
import { MessagingRealtimeService } from './messaging-realtime.service';
import { MessagingRouteService } from './messaging-route.service';
import { MessagingStoreService } from './messaging-store.service';
import { MessagingParticipant, MessagingThread, MessagingThreadMessage } from './messaging.models';

@Component({
  selector: 'app-messaging-thread-page',
  standalone: true,
  imports: [AsyncPipe, DatePipe, KeyValuePipe, ReactiveFormsModule, MatButtonModule, MatCardModule, MatChipsModule, MatDividerModule, MatFormFieldModule, MatIconModule, MatInputModule, MatProgressSpinnerModule],
  templateUrl: './messaging-thread-page.component.html',
  styleUrl: './messaging-thread-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MessagingThreadPageComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly backendEndPoint = inject(backendEndPointToken);
  private readonly store = inject(MessagingStoreService);
  private readonly contextService = inject(MessagingContextService);
  private readonly realtimeService = inject(MessagingRealtimeService);
  private readonly messagesService = inject(MessagingMessagesService);
  private readonly routeService = inject(MessagingRouteService);

  readonly currentThread$ = new BehaviorSubject<MessagingThread | null>(null);
  readonly messages$ = new BehaviorSubject<MessagingThreadMessage[]>([]);
  readonly searchRules$ = new BehaviorSubject<string[]>([]);
  readonly loading$ = new BehaviorSubject<boolean>(true);
  readonly sending$ = new BehaviorSubject<boolean>(false);
  readonly sendError$ = new BehaviorSubject<string | null>(null);
  readonly replyControl = new FormControl('', { nonNullable: true });

  ngOnInit(): void {
    this.store.bootstrap();
    const context = this.contextService.getCurrentContext();

    const threadId$ = combineLatest([
      this.route.paramMap.pipe(map((params) => params.get('threadId'))),
      this.route.queryParamMap.pipe(map((params) => params.get('threadId')))
    ]).pipe(
      map(([paramThreadId, queryThreadId]) => paramThreadId || queryThreadId || null),
      distinctUntilChanged()
    );

    threadId$
      .pipe(
        takeUntil(this.destroy$),
        tap((threadId) => {
          this.loading$.next(true);
          this.store.setActiveThread(threadId);
        }),
        switchMap((threadId) => {
          if (!threadId) return of(null);
          return combineLatest([
            this.store.threads$,
            this.realtimeService.watchThread(threadId, context)
          ]).pipe(
            map(([threads, liveThread]) => threads.find((thread) => thread.threadId === threadId) ?? liveThread ?? null)
          );
        })
      )
      .subscribe((thread) => this.currentThread$.next(thread));

    this.currentThread$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((left, right) => left?.threadId === right?.threadId),
        switchMap((thread) => {
          if (!thread) {
            this.messages$.next([]);
            this.searchRules$.next([]);
            this.loading$.next(false);
            return of(null);
          }

          const contract = this.messagesService.buildCommunicationSearchContract(thread, context);
          this.searchRules$.next(contract.rules);

          return this.messagesService.listThreadMessages(thread, context).pipe(
            tap(() => {
              if ((thread as any)?.unreadCount > 0) {
                this.store.markThreadRead(thread.threadId).subscribe({
                  error: (error) => console.error('Failed to mark thread as read', error)
                });
              }
            }),
            catchError((error) => {
              console.error('Failed to load thread messages', error);
              return of([] as MessagingThreadMessage[]);
            }),
            map((messages) => ({ messages }))
          );
        })
      )
      .subscribe((result) => {
        if (!result) return;
        this.messages$.next(result.messages);
        this.loading$.next(false);
      });
  }

  openContext(thread: MessagingThread): void {
    const route = this.routeService.resolveContextRoute(thread);
    if (!route?.routePath) return;
    void this.router.navigate([route.routePath], { queryParams: route.queryParams });
  }

  goBackToInbox(): void {
    void this.router.navigate(['/app/messaging']);
  }

  sendReply(): void {
    const thread = this.currentThread$.getValue();
    const text = this.replyControl.value.trim();
    if (!thread || !text || this.sending$.getValue()) {
      return;
    }

    const context = this.contextService.getCurrentContext();
    const sender = this.resolveSender(thread, context.participantId);
    const sentAt = new Date().toISOString();
    const routePayload = {
      screen: 'MessagingThread',
      routePath: '/app/messaging/thread',
      queryParams: { threadId: thread.threadId },
      threadId: thread.threadId,
      resourceType: thread.context?.contextType,
      resourceId: thread.context?.contextId
    };

    this.sending$.next(true);
    this.sendError$.next(null);

    this.createReplyCommunication(thread, sender, text, sentAt).pipe(
      switchMap((communication) => this.store.notifyThreadMessage(thread.threadId, {
        organizationId: context.organizationId,
        sender,
        context: thread.context,
        subject: thread.subject,
        message: {
          communicationId: communication?.id,
          preview: text,
          text,
          sentAt,
          route: routePayload,
          metadata: {
            threadType: thread.threadType,
            contextType: thread.context?.contextType,
            communicationSender: 'user'
          }
        }
      })),
      switchMap(() => this.messagesService.listThreadMessages(thread, context)),
      catchError((error) => {
        console.error('Failed to send thread reply', error);
        this.sendError$.next('Unable to send this reply right now.');
        return of(null);
      })
    ).subscribe((messages) => {
      if (messages) {
        this.messages$.next(messages);
        this.replyControl.setValue('');
        this.store.loadThreads();
        this.store.loadNotifications();
      }
      this.sending$.next(false);
    });
  }

  hasReplyText(): boolean {
    return this.replyControl.value.trim().length > 0;
  }

  private resolveSender(thread: MessagingThread, participantId: string): MessagingParticipant {
    const threadParticipant = (thread.participants ?? []).find((participant) => participant.participantId === participantId);
    return threadParticipant ?? this.contextService.getCurrentParticipant();
  }

  private createReplyCommunication(thread: MessagingThread, sender: MessagingParticipant, text: string, sentAt: string) {
    const senderReference = sender.resourceType && (sender.resourceId || sender.participantId)
      ? `${sender.resourceType}/${sender.resourceId || sender.participantId}`
      : undefined;

    const recipients = (thread.participants ?? [])
      .filter((participant) => participant.participantId !== sender.participantId)
      .filter((participant) => participant.resourceType && (participant.resourceId || participant.participantId))
      .map((participant) => ({
        reference: `${participant.resourceType}/${participant.resourceId || participant.participantId}`,
        display: participant.display
      }));

    const basedOnReference = thread.context?.serviceRequestId
      ? `ServiceRequest/${thread.context.serviceRequestId}`
      : thread.context?.contextType && thread.context?.contextId
        ? `${this.normalizeResourceType(thread.context.contextType)}/${thread.context.contextId}`
        : undefined;
    const subjectReference = thread.subject?.resourceType && thread.subject?.resourceId
      ? `${thread.subject.resourceType}/${thread.subject.resourceId}`
      : undefined;

    const communication: Communication = {
      resourceType: 'Communication',
      status: 'completed',
      sent: sentAt,
      basedOn: basedOnReference ? [{ reference: basedOnReference }] : undefined,
      subject: subjectReference ? { reference: subjectReference, display: thread.subject?.display } : undefined,
      sender: senderReference ? { reference: senderReference, display: sender.display } : undefined,
      recipient: recipients.length ? recipients : undefined,
      payload: [{ contentString: text }]
    };

    return this.http.post<Communication>(`${this.backendEndPoint}/Communication`, communication, {
      headers: new HttpHeaders({ 'Content-Type': 'application/fhir+json' })
    }).pipe(
      catchError((error) => {
        console.error('Failed to create reply Communication resource', error);
        return of(null);
      })
    );
  }

  private normalizeResourceType(value?: string): string {
    const normalized = `${value ?? ''}`.trim();
    if (!normalized) {
      return '';
    }

    return normalized
      .split('-')
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join('');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
